import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Tag,
  Tooltip,
  Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const SizesTab = ({ itemId }) => {
  const navigate = useNavigate();
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (itemId) {
      fetchSizes();
    }
  }, [itemId]);

  const fetchSizes = async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.urls.bot.sizeVariants}?bot_item=${itemId}`,
        null,
        navigate,
      );

      if (response.ok) {
        setSizes(response.data.results || response.data || []);
      } else {
        Utilities.showError(response, "Failed to fetch size variants");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sizeId) => {
    try {
      const response = await Utilities.fetchWithAuth(
        "DELETE",
        domainSettings.urls.bot.sizeVariantDetail.replace(":id", sizeId),
        null,
        navigate,
      );

      if (response.ok) {
        message.success("Size variant deleted successfully");
        fetchSizes();
      } else {
        Utilities.showError(response, "Failed to delete size variant");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        bot_item: itemId,
        size_name: values.size_name,
        size_code: values.size_code,
        is_active: values.is_active ? 1 : 0,
      };

      const method = editingSize ? "PUT" : "POST";
      const url = editingSize
        ? domainSettings.urls.bot.sizeVariantDetail.replace(
            ":id",
            editingSize.id,
          )
        : domainSettings.urls.bot.sizeVariants;

      const response = await Utilities.fetchWithAuth(
        method,
        url,
        payload,
        navigate,
      );

      if (response.ok) {
        message.success(
          editingSize
            ? "Size variant updated successfully"
            : "Size variant created successfully",
        );
        setModalVisible(false);
        setEditingSize(null);
        form.resetFields();
        fetchSizes();
      } else {
        Utilities.showError(response, "Failed to save size variant");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (size) => {
    setEditingSize(size);
    form.setFieldsValue({
      size_name: size.size_name,
      size_code: size.size_code,
      is_active: size.is_active === 1,
    });
    setModalVisible(true);
  };

  const columns = [
    {
      title: "Size Name",
      dataIndex: "size_name",
      key: "size_name",
    },
    {
      title: "Size Code",
      dataIndex: "size_code",
      key: "size_code",
      render: (code) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => (
        <Tag color={isActive === 1 ? "green" : "red"}>
          {isActive === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this size variant?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingSize(null);
            form.resetFields();
            form.setFieldsValue({ is_active: true });
            setModalVisible(true);
          }}
        >
          Add Size Variant
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={sizes}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      {/* Size Variant Form Modal */}
      <Modal
        title={editingSize ? "Edit Size Variant" : "Add Size Variant"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingSize(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true }}
        >
          <Form.Item
            label="Size Name"
            name="size_name"
            rules={[
              { required: true, message: "Please enter size name" },
              { max: 50, message: "Name cannot exceed 50 characters" },
            ]}
          >
            <Input placeholder="e.g., Small, Medium, Large, X-Large" />
          </Form.Item>

          <Form.Item
            label="Size Code"
            name="size_code"
            rules={[
              { required: true, message: "Please enter size code" },
              { max: 10, message: "Code cannot exceed 10 characters" },
            ]}
          >
            <Input
              placeholder="e.g., S, M, L, XL"
              style={{ textTransform: "uppercase" }}
              onChange={(e) => {
                form.setFieldValue("size_code", e.target.value.toUpperCase());
              }}
            />
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SizesTab;
